import React from 'react';
import type { FinancialRiskFinding } from '../../services/api';

interface SectionSummary {
  key: string; // articleNumber-sectionNumber
  articleNumber: string;
  sectionNumber: string;
  sectionTitle: string;
  findingCount: number;
}

interface FinancialRiskSectionListProps {
  findings: FinancialRiskFinding[];
  selectedSectionKey: string | null;
  onSelectSection: (key: string) => void;
}

function buildSectionSummaries(findings: FinancialRiskFinding[]): SectionSummary[] {
  const map = new Map<string, SectionSummary>();

  for (const f of findings) {
    const key = `${f.articleNumber}-${f.sectionNumber}`;
    const existing = map.get(key);
    if (existing) {
      existing.findingCount++;
    } else {
      map.set(key, {
        key,
        articleNumber: f.articleNumber,
        sectionNumber: f.sectionNumber,
        sectionTitle: f.sectionTitle || '',
        findingCount: 1,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    if (a.articleNumber !== b.articleNumber) {
      return a.articleNumber.localeCompare(b.articleNumber, undefined, { numeric: true });
    }
    return a.sectionNumber.localeCompare(b.sectionNumber, undefined, { numeric: true });
  });
}

export const FinancialRiskSectionList: React.FC<FinancialRiskSectionListProps> = ({
  findings,
  selectedSectionKey,
  onSelectSection,
}) => {
  const sections = buildSectionSummaries(findings);

  if (sections.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400" data-testid="no-sections">
        No sections with findings.
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="section-list">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
        Sections with Findings
      </h3>
      {sections.map((section) => {
        const isSelected = selectedSectionKey === section.key;
        return (
          <button
            key={section.key}
            onClick={() => onSelectSection(section.key)}
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
              isSelected
                ? 'bg-primary-50 border border-primary-200 text-primary-900'
                : 'hover:bg-gray-50 border border-transparent text-gray-700'
            }`}
            data-testid={`section-row-${section.key}`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <span className="text-sm font-medium">
                  Art. {section.articleNumber} / Sec. {section.sectionNumber}
                </span>
                {section.sectionTitle && (
                  <p className="text-xs text-gray-500 truncate">{section.sectionTitle}</p>
                )}
              </div>
              <span
                className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  isSelected ? 'bg-primary-200 text-primary-800' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {section.findingCount}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
